// useHospitalBriefingVideo — resolves the per-hospital Dignity briefing video
// (YouTube embed or an MP4 we host) for the picture-in-picture player on the
// hospital page and the briefing slot on the Quality report card.
//
// Source of truth: the static per-hospital JSON's `meta.briefing_video`
// object, written by scripts/generate_hospital_detail_cache.py. As of
// 2026-09-15 no table, column, or URL pattern produces hospital briefings
// yet (dignity_narration_lines is keyed by scenario/procedure, video_pipeline
// by scenario), so the generator does not emit the field and this hook
// returns null everywhere. When the video pipeline starts cutting hospital
// briefings, populate `meta.briefing_video` there — nothing on the page
// needs to change.
//
// `?briefing=` on the URL overrides the resolver so a briefing can be
// previewed before the pipeline exists: `?briefing=yt:<youtubeId>` or
// `?briefing=https://…/file.mp4`.
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

export interface HospitalBriefingVideo {
  /** YouTube video id — rendered through youtube-nocookie.com. */
  youtubeId?: string | null;
  /** Self-hosted MP4 (CDN URL). Takes precedence over youtubeId when both exist. */
  mp4Url?: string | null;
  posterUrl?: string | null;
  title?: string | null;
  description?: string | null;
  /** ISO date the briefing was recorded / the CMS release it narrates. */
  publishedAt?: string | null;
  durationSec?: number | null;
}

// Snake-case shape as the generator writes it into meta.briefing_video.
interface StaticBriefingMeta {
  youtube_id?: string | null;
  mp4_url?: string | null;
  poster_url?: string | null;
  title?: string | null;
  description?: string | null;
  published_at?: string | null;
  duration_sec?: number | null;
}

export function parseBriefingOverride(raw: string | null): HospitalBriefingVideo | null {
  if (!raw) return null;
  const v = raw.trim();
  const yt = v.match(/^yt:([A-Za-z0-9_-]{6,20})$/);
  if (yt) return { youtubeId: yt[1], title: null, publishedAt: null };
  if (/^https:\/\/[^\s]+\.(mp4|webm|m4v)(\?.*)?$/i.test(v)) return { mp4Url: v, title: null, publishedAt: null };
  return null;
}

export function useHospitalBriefingVideo(staticMeta: { briefing_video?: StaticBriefingMeta | null } | null | undefined): HospitalBriefingVideo | null {
  const { search } = useLocation();
  return useMemo(() => {
    const override = parseBriefingOverride(new URLSearchParams(search).get("briefing"));
    if (override) return override;
    const m = staticMeta?.briefing_video;
    if (!m || (!m.youtube_id && !m.mp4_url)) return null;
    return {
      youtubeId: m.youtube_id ?? null,
      mp4Url: m.mp4_url ?? null,
      posterUrl: m.poster_url ?? null,
      title: m.title ?? null,
      description: m.description ?? null,
      publishedAt: m.published_at ?? null,
      durationSec: m.duration_sec ?? null,
    };
  }, [search, staticMeta]);
}

/** Poster to show before play: the explicit poster, else YouTube's own thumbnail. */
export function briefingPoster(v: HospitalBriefingVideo): string | null {
  if (v.posterUrl) return v.posterUrl;
  if (v.youtubeId) return `https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`;
  return null;
}

/** ISO-8601 duration ("PT2M31S") for VideoObject markup. */
export function isoDuration(sec: number | null | undefined): string | null {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return null;
  const s = Math.round(sec);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return `PT${h ? `${h}H` : ""}${m ? `${m}M` : ""}${r || (!h && !m) ? `${r}S` : ""}`;
}
